import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { createReservation, createReservationAsUser, getUpcomingEvents } from '../services/reservationService';
import { getZonesByRestaurant } from '../services/zoneService';
import { getShiftsForDate, getSlotAvailability } from '../services/shiftService';
import { getEventsByRestaurant } from '../services/eventService';
import { getBlockedDates } from '../services/restaurantService';
import { getEventTicketTypes, reserveEventTickets, calculateOrderTotal } from '../services/eventTicketService';
import { sendGuestOtp, verifyGuestOtp, resendGuestOtp } from '../services/guestEmailVerificationService';
import { useAuth } from '../context/AuthContext';
import { toLocalDateString } from '../utils/dateUtils';
import EventPopup from './EventPopup';
import EventCard from './EventCard';
import EventTicketSelector from './EventTicketSelector';
import './EventTicketSelector.css';
import './ReservationForm.css';

const ReservationForm = ({ restaurantId, restaurantName, restaurant, isWidget = false, initialEvent = null, onInitialEventHandled = null, onSuccessChange = null, onEventDateChange = null, onEventHouseRulesChange = null }) => {
  const { isLoggedIn, user } = useAuth();
  const navigate = useNavigate();
  const formContainerRef = useRef(null);
  const timeSlotsContainerRef = useRef(null);
  const quickDatesContainerRef = useRef(null);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [submittedRequiresApproval, setSubmittedRequiresApproval] = useState(false);
  const [submittedToWaitlist, setSubmittedToWaitlist] = useState(false);
  const [submittedPaymentPending, setSubmittedPaymentPending] = useState(false);
  const [waitlistInfo, setWaitlistInfo] = useState({ position: null, reason: null });
  const [error, setError] = useState('');
  const [events, setEvents] = useState([]);
  const [externalEvents, setExternalEvents] = useState([]);
  const [externalEventsLoaded, setExternalEventsLoaded] = useState(false);
  const [showEventPopup, setShowEventPopup] = useState(false);
  const [selectedEventIndex, setSelectedEventIndex] = useState(0);
  const [zones, setZones] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [blockedDates, setBlockedDates] = useState([]);
  const [soldOutDates, setSoldOutDates] = useState([]);
  const [blockedDatesLoaded, setBlockedDatesLoaded] = useState(false);
  const [nextAvailableDate, setNextAvailableDate] = useState(null);
  const [slotAvailability, setSlotAvailability] = useState({}); // Map of shiftId -> availability data
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const [dateViewMode, setDateViewMode] = useState('quick'); // 'quick' or 'calendar'
  const [calendarToast, setCalendarToast] = useState(null);
  const [showCustomGuestInput, setShowCustomGuestInput] = useState(false);
  const [customGuestValue, setCustomGuestValue] = useState('');

  // Event ticketing state
  const [ticketMode, setTicketMode] = useState(false); // True when purchasing event tickets
  const [selectedEvent, setSelectedEvent] = useState(null); // The event being purchased
  const [selectedTickets, setSelectedTickets] = useState([]); // Selected ticket types with quantities
  const [ticketTotal, setTicketTotal] = useState(0); // Total price for tickets
  const [ticketFeeConfig, setTicketFeeConfig] = useState({ fixedFeeCents: 0, feePercent: 0 }); // Fee config
  const [ticketStep, setTicketStep] = useState(1); // 1: Select tickets, 2: Buyer info, 3: Ticket holders
  const [ticketHolders, setTicketHolders] = useState([]); // Per-ticket holder info
  const [ticketSubmitSuccess, setTicketSubmitSuccess] = useState(false); // Ticket submission success
  const [ticketAutoApproved, setTicketAutoApproved] = useState(false); // Auto-approved (no approval needed)

  // Booking type selection state (inline, not modal)
  const [bookingTypeChoice, setBookingTypeChoice] = useState(null); // null, 'regular', or 'tickets'
  const [bookingTypeEvents, setBookingTypeEvents] = useState([]); // Non-blocking events for the selected date

  // Step 1 data
  const [reservationDetails, setReservationDetails] = useState({
    date: '',
    time: '',
    guests: '',
    specialOccasion: '',
    eventId: null,
  });

  // Special occasion checkbox state
  const [hasSpecialOccasion, setHasSpecialOccasion] = useState(false);

  // Collapsible sections state
  const [allergiesExpanded, setAllergiesExpanded] = useState(false);
  const [dietaryExpanded, setDietaryExpanded] = useState(false);

  // Email OTP verification state (event tickets only)
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [emailOtp, setEmailOtp] = useState('');
  const [emailOtpLoading, setEmailOtpLoading] = useState(false);
  const [emailOtpError, setEmailOtpError] = useState('');
  const [verifiedEmail, setVerifiedEmail] = useState('');

  // Step 2 data
  const [personalInfo, setPersonalInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    allergies: [],
    dietaryPreference: [],
    comments: '',
  });

  // Notify parent when success state changes (used by widget to hide notes)
  useEffect(() => {
    if (onSuccessChange) {
      onSuccessChange(success);
    }
  }, [success, onSuccessChange]);

  useEffect(() => {
    if (onEventDateChange) {
      // While events are still loading, treat as event date to prevent flash
      onEventDateChange(!externalEventsLoaded || bookingTypeEvents.length > 0);
    }
    if (onEventHouseRulesChange) {
      const firstEvent = bookingTypeEvents.length > 0 ? bookingTypeEvents[0] : null;
      onEventHouseRulesChange(firstEvent?.houseRules || '');
    }
  }, [bookingTypeEvents, externalEventsLoaded, onEventDateChange, onEventHouseRulesChange]);

  // Helper to check if a date is blocked
  const isDateBlocked = (dateStr) => {
    return blockedDates.includes(dateStr);
  };

  // Helper to check if a date is sold out
  const isDateSoldOut = (dateStr) => {
    return soldOutDates.includes(dateStr);
  };

  // Helper to get external events for a specific date
  const getEventsForDate = (dateStr) => {
    return externalEvents.filter(event => event.date === dateStr);
  };

  // Check if a date has an event that blocks regular reservations
  const hasBlockingEvent = (dateStr) => {
    const events = getEventsForDate(dateStr);
    return events.some(event => event.blocksRegularReservations && event.isActive);
  };

  // Get all blocking events for a date
  const getBlockingEvents = (dateStr) => {
    const events = getEventsForDate(dateStr);
    return events.filter(event => event.blocksRegularReservations && event.isActive);
  };

  // Get non-blocking events for a date
  const getNonBlockingEvents = (dateStr) => {
    const events = getEventsForDate(dateStr);
    return events.filter(event => !event.blocksRegularReservations && event.isActive);
  };

  // Handle clicking the EVENT badge on a date pill
  const handleEventBadgeClick = (e, dateStr) => {
    e.stopPropagation();

    if (isDateSoldOut(dateStr)) {
      handleStep1Change('date', dateStr);
      return;
    }

    // First check for blocking events - show ticket selector inline (no regular reservation option)
    const blockingEvents = getBlockingEvents(dateStr);
    if (blockingEvents.length > 0) {
      setReservationDetails(prev => ({ ...prev, date: dateStr }));
      setBookingTypeEvents(blockingEvents);
      setBookingTypeChoice('tickets');
      setSelectedTickets([]);
      setTicketTotal(0);
      // Auto-select only when there's exactly one blocking event
      if (blockingEvents.length === 1) {
        setSelectedEvent(blockingEvents[0]);
      } else {
        setSelectedEvent(null);
      }
      return;
    }

    // For non-blocking events or regular dates, just set the date
    // handleStep1Change will automatically set up booking type selector if needed
    handleStep1Change('date', dateStr);
  };

  // Handle user choosing regular reservation (inline)
  const handleSelectRegularReservation = () => {
    setBookingTypeChoice('regular');
  };

  // Handle user choosing event tickets (inline)
  const handleSelectEventTickets = (event) => {
    setBookingTypeChoice('tickets');
    setSelectedEvent(event);
    // Reset ticket selection state but don't enter full ticketMode
    setSelectedTickets([]);
    setTicketTotal(0);
  };

  // Handle ticket selection change from EventTicketSelector
  const handleTicketSelectionChange = (tickets, total, fees) => {
    setSelectedTickets(tickets);
    setTicketTotal(total);
    if (fees) setTicketFeeConfig(fees);
  };

  // Initialize ticket holders array when moving to step 3
  const initializeTicketHolders = () => {
    const holders = [];
    let globalIndex = 0;
    selectedTickets.forEach(ticket => {
      for (let i = 0; i < ticket.quantity; i++) {
        const isFirstHolder = globalIndex === 0;
        holders.push({
          ticketTypeId: ticket.ticketTypeId,
          ticketTypeName: ticket.ticketTypeName,
          // Pre-populate first holder with buyer info
          firstName: isFirstHolder ? personalInfo.firstName : '',
          lastName: isFirstHolder ? personalInfo.lastName : '',
          email: isFirstHolder ? personalInfo.email : '',
          phone: isFirstHolder ? personalInfo.phone : '',
          socialMediaLink: '',
          sameAsBuyer: isFirstHolder // Only first ticket overall defaults to buyer info
        });
        globalIndex++;
      }
    });
    setTicketHolders(holders);
  };

  // Handle ticket holder info change
  const handleTicketHolderChange = (index, field, value) => {
    const newHolders = [...ticketHolders];
    newHolders[index] = { ...newHolders[index], [field]: value };

    // If "same as buyer" is checked, copy buyer info
    if (field === 'sameAsBuyer' && value) {
      newHolders[index] = {
        ...newHolders[index],
        firstName: personalInfo.firstName,
        lastName: personalInfo.lastName,
        email: personalInfo.email,
        phone: personalInfo.phone,
        sameAsBuyer: true
      };
    }

    setTicketHolders(newHolders);
  };

  // Submit ticket reservation
  const handleTicketSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      const payload = {
        tickets: selectedTickets.flatMap(ticket => {
          const holders = ticketHolders.filter(h => h.ticketTypeId === ticket.ticketTypeId);
          return holders.map(holder => ({
            ticketTypeId: ticket.ticketTypeId,
            firstName: holder.firstName,
            lastName: holder.lastName,
            email: holder.email,
            phone: holder.phone,
            socialMediaLink: holder.socialMediaLink || undefined
          }));
        }),
        buyerFirstName: personalInfo.firstName,
        buyerLastName: personalInfo.lastName,
        buyerEmail: personalInfo.email,
        buyerPhone: personalInfo.phone
      };

      const response = await reserveEventTickets(selectedEvent.id, payload);

      // Auto-approved with payment required — redirect to payment page
      if (response.autoApproved && response.paymentUrl) {
        const token = response.order?.paymentToken;
        if (token) {
          navigate(`/event-payment/${token}`);
          return;
        }
      }

      setTicketSubmitSuccess(true);
      setTicketAutoApproved(response.autoApproved || false);
      setLoading(false);
      scrollToTop();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit ticket reservation. Please try again.');
      setLoading(false);
      scrollToTop();
    }
  };

  // Exit ticket mode and reset
  const exitTicketMode = () => {
    setTicketMode(false);
    setSelectedEvent(null);
    setSelectedTickets([]);
    setTicketTotal(0);
    setTicketStep(1);
    setTicketHolders([]);
    setTicketSubmitSuccess(false);
    setTicketAutoApproved(false);
  };

  // Quick date options
  const getQuickDates = () => {
    const dates = [];
    for (let i = 0; i < 14; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
      const monthDay = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const dateValue = toLocalDateString(date);
      dates.push({
        weekday,
        monthDay,
        value: dateValue,
        isBlocked: isDateBlocked(dateValue),
        isSoldOut: isDateSoldOut(dateValue),
        hasEvent: getEventsForDate(dateValue).length > 0,
      });
    }
    return dates;
  };

  // Check if all quick dates are blocked
  const allQuickDatesBlocked = () => {
    const quickDates = getQuickDates();
    return quickDates.every(d => d.isBlocked);
  };

  // Generate time slots from start to end time with given interval
  // Handles overnight shifts (e.g., 9 PM to 2 AM)
  const generateTimeSlots = (startTime, endTime, interval = 30) => {
    if (!startTime || !endTime) return [];

    const slots = [];
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);

    const startMinutes = startHour * 60 + startMin;
    let endMinutes = endHour * 60 + endMin;

    // Handle overnight shifts (e.g., 21:00 to 02:00)
    if (endMinutes <= startMinutes) {
      endMinutes += 24 * 60; // Add 24 hours
    }

    let currentMinutes = startMinutes;

    while (currentMinutes < endMinutes) {
      // Normalize to handle times past midnight
      const normalizedMinutes = currentMinutes % (24 * 60);
      const hour = Math.floor(normalizedMinutes / 60);
      const min = normalizedMinutes % 60;
      const timeStr = `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
      slots.push(timeStr);
      currentMinutes += interval;
    }

    return slots;
  };

  // Helper to convert HH:mm to minutes
  const timeToMinutes = (time) => {
    const [hours, mins] = time.split(':').map(Number);
    return hours * 60 + mins;
  };

  // Simple sort: daytime slots first (6am-11:59pm), then late-night slots (12am-5:59am) at the end
  const sortTimeSlotsWithLateNightLast = (slots) => {
    if (!slots || slots.length === 0) return slots;

    const SIX_AM = 6 * 60; // 360 minutes

    return [...slots].sort((a, b) => {
      const [aH, aM] = a.split(':').map(Number);
      const [bH, bM] = b.split(':').map(Number);
      let aMinutes = aH * 60 + aM;
      let bMinutes = bH * 60 + bM;

      // Late-night slots (before 6am) get pushed to the end by adding 24 hours
      if (aMinutes < SIX_AM) aMinutes += 24 * 60;
      if (bMinutes < SIX_AM) bMinutes += 24 * 60;

      return aMinutes - bMinutes;
    });
  };

  // Check if time slot is after midnight (for auto-date-change feature)
  const isNextDaySlot = (timeSlot) => {
    const SIX_AM = 6 * 60;
    const [h, m] = timeSlot.split(':').map(Number);
    const minutes = h * 60 + m;
    return minutes < SIX_AM; // Slots before 6am are considered "next day"
  };

  // Check if a time falls within any shift's time range
  // Handles overnight shifts (e.g., 9 PM to 2 AM)
  const isTimeInShift = (time) => {
    if (!shifts || shifts.length === 0) return false;
    const timeMinutes = timeToMinutes(time);
    return shifts.some(shift => {
      const startMinutes = timeToMinutes(shift.shiftStartTime);
      let endMinutes = timeToMinutes(shift.shiftEndTime);

      // Handle overnight shifts
      if (endMinutes <= startMinutes) {
        // Shift spans midnight - time is in shift if:
        // - time is after start (e.g., 21:00-23:59), OR
        // - time is before end (e.g., 00:00-02:00)
        return timeMinutes >= startMinutes || timeMinutes < endMinutes;
      }
      return timeMinutes >= startMinutes && timeMinutes < endMinutes;
    });
  };

  // Get combined time slots: restaurant hours + shift slots (with correct intervals)
  const getCombinedTimeSlots = () => {
    const slotInterval = restaurant?.slotInterval || 30;
    const openingTime = restaurant?.openingTime || '09:00';
    const closingTime = restaurant?.closingTime || '22:00';

    const allSlotsSet = new Set();

    // If we have slotAvailability data, use shift slots from backend (correct intervals)
    if (Object.keys(slotAvailability).length > 0) {
      Object.values(slotAvailability).forEach(availability => {
        if (availability.slots) {
          availability.slots.forEach(slot => {
            const time = slot.time.substring(0, 5);
            allSlotsSet.add(time);
          });
        }
      });
    } else if (shifts && shifts.length > 0) {
      // Fallback: generate shift slots client-side
      shifts.forEach(shift => {
        const shiftInterval = shift.slotInterval || slotInterval;
        const shiftSlots = generateTimeSlots(shift.shiftStartTime, shift.shiftEndTime, shiftInterval);
        shiftSlots.forEach(slot => allSlotsSet.add(slot));
      });
    }

    // Add restaurant base slots only when there are no shifts
    if (!shifts || shifts.length === 0) {
      const baseSlots = generateTimeSlots(openingTime, closingTime, slotInterval);
      baseSlots.forEach(slot => allSlotsSet.add(slot));
    }

    // Convert to array and sort: daytime first, late-night (after midnight) at the end
    const combinedSlots = sortTimeSlotsWithLateNightLast(Array.from(allSlotsSet));

    return combinedSlots;
  };

  // Get visible shifts (those with showLabelToCustomers enabled)
  const getVisibleShifts = () => {
    if (!shifts || shifts.length === 0) return [];
    return shifts.filter(shift => shift.showLabelToCustomers);
  };

  // Get shift for a given time slot (check ALL shifts, not just visible ones)
  // Handles overnight shifts (e.g., 9 PM to 2 AM)
  const getShiftForTime = (timeSlot) => {
    if (!shifts || shifts.length === 0) return null;

    const [slotHour, slotMin] = timeSlot.split(':').map(Number);
    const slotMinutes = slotHour * 60 + slotMin;

    for (const shift of shifts) {
      const [startHour, startMin] = shift.shiftStartTime.split(':').map(Number);
      const [endHour, endMin] = shift.shiftEndTime.split(':').map(Number);
      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;

      // Handle overnight shifts
      if (endMinutes <= startMinutes) {
        // Shift spans midnight - time is in shift if after start OR before end
        if (slotMinutes >= startMinutes || slotMinutes < endMinutes) {
          return shift;
        }
      } else if (slotMinutes >= startMinutes && slotMinutes < endMinutes) {
        return shift;
      }
    }
    return null;
  };

  // Get shift label for a given time slot (if any visible shift covers that time)
  // Handles overnight shifts (e.g., 9 PM to 2 AM)
  const getShiftLabelForTime = (timeSlot) => {
    const visibleShifts = getVisibleShifts();
    if (visibleShifts.length === 0) return null;

    const [slotHour, slotMin] = timeSlot.split(':').map(Number);
    const slotMinutes = slotHour * 60 + slotMin;

    for (const shift of visibleShifts) {
      const [startHour, startMin] = shift.shiftStartTime.split(':').map(Number);
      const [endHour, endMin] = shift.shiftEndTime.split(':').map(Number);
      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;

      // Handle overnight shifts
      if (endMinutes <= startMinutes) {
        // Shift spans midnight - time is in shift if after start OR before end
        if (slotMinutes >= startMinutes || slotMinutes < endMinutes) {
          return shift.label;
        }
      } else if (slotMinutes >= startMinutes && slotMinutes < endMinutes) {
        return shift.label;
      }
    }
    return null;
  };

  // Get slot availability info for a given time slot
  const getSlotInfo = (timeSlot, guestCount = null) => {
    const shift = getShiftForTime(timeSlot);
    if (!shift) return { isBlocked: false, remainingCapacity: null, isManuallyBlocked: false, isOverCapacity: false, waitlistCount: 0, allowOverbooking: false, requiresApproval: false, maxAllowedGuestsNumber: null, shiftRemainingCovers: null };

    // Check if slot requires approval
    const slotTime = timeSlot.substring(0, 5); // Normalize to HH:mm
    const requiresApproval = shift.slotsRequiringApproval?.includes(slotTime) || false;

    const availability = slotAvailability[shift.id];
    if (!availability || !availability.slots) return { isBlocked: false, remainingCapacity: null, isManuallyBlocked: false, isOverCapacity: false, waitlistCount: 0, allowOverbooking: false, requiresApproval, maxAllowedGuestsNumber: availability?.maxAllowedGuestsNumber || null, shiftRemainingCovers: availability?.shiftRemainingCovers || null };

    // Find the slot info for this time
    const slotInfo = availability.slots.find(s => s.time === timeSlot || s.time === `${timeSlot}:00`);
    if (!slotInfo) return { isBlocked: false, remainingCapacity: null, isManuallyBlocked: false, isOverCapacity: false, waitlistCount: 0, allowOverbooking: false, requiresApproval, maxAllowedGuestsNumber: availability.maxAllowedGuestsNumber || null, shiftRemainingCovers: availability.shiftRemainingCovers || null };

    // Check if manually blocked by admin - only manually blocked slots are truly blocked
    const isManuallyBlocked = slotInfo.isManuallyBlocked || false;
    const isBlocked = slotInfo.isBlocked || false;

    // Check if this is an over-capacity slot (booking goes to waitlist)
    const isOverCapacity = slotInfo.isOverCapacity || false;
    const waitlistCount = slotInfo.waitlistCount || 0;

    return {
      isBlocked,
      isManuallyBlocked,
      remainingCapacity: slotInfo.remainingCapacity,
      bookedGuests: slotInfo.bookedGuests,
      maxCapacity: slotInfo.maxCapacity,
      allowOverbooking: availability.allowOverbooking,
      isOverCapacity,
      waitlistCount,
      requiresApproval,
      maxAllowedGuestsNumber: availability.maxAllowedGuestsNumber || null,
      shiftRemainingCovers: availability.shiftRemainingCovers || null,
      shiftTotalCovers: availability.shiftTotalCovers || 0,
      shiftMaxCapacity: availability.shiftMaxCapacity || null,
    };
  };

  // Get max allowed guests from shifts (for guest selector limit)
  const getMaxGuestsFromShifts = () => {
    if (Object.keys(slotAvailability).length === 0) return 10; // Default before data loads

    let maxGuests = null;
    Object.values(slotAvailability).forEach(availability => {
      if (availability.maxAllowedGuestsNumber) {
        if (maxGuests === null || availability.maxAllowedGuestsNumber < maxGuests) {
          maxGuests = availability.maxAllowedGuestsNumber;
        }
      }
    });

    return maxGuests || 10; // Default to 10 if no max is set
  };

  const maxGuestsForPills = getMaxGuestsFromShifts();

  const visibleShifts = getVisibleShifts();

  // Filter out past times if selected date is today
  const filterPastTimes = (slots) => {
    if (!reservationDetails.date) return slots;

    const todayStr = toLocalDateString(new Date());

    // If selected date is not today, show all slots
    if (reservationDetails.date !== todayStr) {
      return slots;
    }

    // If selected date is today, filter out past times
    const now = new Date();
    const currentHour = now.getHours();
    const currentMin = now.getMinutes();

    return slots.filter(timeSlot => {
      const [slotHour, slotMin] = timeSlot.split(':').map(Number);

      // Late-night slots (before 6 AM) are for the next day, so always show them
      if (isNextDaySlot(timeSlot)) return true;

      // Compare time for regular slots
      if (slotHour > currentHour) return true;
      if (slotHour === currentHour && slotMin > currentMin) return true;

      return false;
    });
  };

  const allTimeSlots = getCombinedTimeSlots();
  const filteredPastTimeSlots = filterPastTimes(allTimeSlots);
  // Filter out slots that are manually blocked by admin (hidden from customer view)
  const timeSlots = filteredPastTimeSlots.filter(time => !getSlotInfo(time).isManuallyBlocked);

  // Format time to 12-hour format
  const formatTime = (time24) => {
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'pm' : 'am';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Scroll to top when form mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    // Fetch upcoming events (internal)
    const fetchEvents = async () => {
      try {
        const eventsData = await getUpcomingEvents(restaurantId, 5);
        setEvents(eventsData);
      } catch (err) {
        console.error('Error fetching events:', err);
      }
    };
    fetchEvents();

    // Fetch external events from events platform
    const fetchExternalEvents = async () => {
      try {
        const eventsData = await getEventsByRestaurant(restaurantId);
        const eventsList = eventsData.events || [];
        setExternalEvents(eventsList);
        setExternalEventsLoaded(true);
      } catch (err) {
        console.error('Error fetching external events:', err);
        setExternalEventsLoaded(true);
      }
    };
    fetchExternalEvents();

    // Fetch zones for the restaurant
    const fetchZones = async () => {
      try {
        const zonesData = await getZonesByRestaurant(restaurantId);
        setZones(zonesData || []);
      } catch (err) {
        console.error('Error fetching zones:', err);
        setZones([]);
      }
    };
    fetchZones();

    // Fetch blocked dates and sold out dates for the restaurant
    const fetchBlockedDates = async () => {
      try {
        const data = await getBlockedDates(restaurantId);
        setBlockedDates(data.blockedDates || []);
        setSoldOutDates(data.soldOutDates || []);
        setNextAvailableDate(data.nextAvailableDate);
        setBlockedDatesLoaded(true);
      } catch (err) {
        console.error('Error fetching blocked dates:', err);
        setBlockedDates([]);
        setSoldOutDates([]);
        setBlockedDatesLoaded(true);
      }
    };
    fetchBlockedDates();
  }, [restaurantId]);

  // Handle initialEvent passed from parent (e.g. RestaurantDetail)
  useEffect(() => {
    if (!initialEvent) return;
    const eventDate = initialEvent.eventDate
      ? toLocalDateString(new Date(initialEvent.eventDate))
      : '';
    if (eventDate) {
      setReservationDetails(prev => ({ ...prev, date: eventDate }));
      setBookingTypeChoice('tickets');
      setBookingTypeEvents([initialEvent]);
      setSelectedEvent(initialEvent);
      setSelectedTickets([]);
      setTicketTotal(0);
      setDateViewMode('quick');
      setTimeout(() => {
        if (quickDatesContainerRef.current) {
          const pills = quickDatesContainerRef.current.querySelectorAll('.quick-date-pill');
          for (const pill of pills) {
            if (pill.classList.contains('active')) {
              pill.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
              break;
            }
          }
        }
      }, 150);
    }
    if (onInitialEventHandled) onInitialEventHandled();
  }, [initialEvent]);

  // Auto-select first available date when blocked dates are loaded
  useEffect(() => {
    // Wait until blocked dates have been loaded from API
    if (!blockedDatesLoaded) return;

    // Only auto-select if no date is currently selected
    if (reservationDetails.date) return;

    // Find the first available (non-blocked and non-sold-out) date
    for (let i = 0; i < 14; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      const dateValue = toLocalDateString(date);

      if (!blockedDates.includes(dateValue) && !soldOutDates.includes(dateValue)) {
        setReservationDetails(prev => ({ ...prev, date: dateValue }));

        // Scroll to the selected date pill after a short delay
        setTimeout(() => {
          if (quickDatesContainerRef.current) {
            const datePills = quickDatesContainerRef.current.querySelectorAll('.quick-date-pill');
            if (datePills[i]) {
              datePills[i].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
            }
          }
        }, 100);
        break;
      }
    }
  }, [blockedDatesLoaded, blockedDates, soldOutDates]);

  // Re-evaluate booking type when externalEvents load after date was already auto-selected
  useEffect(() => {
    if (!reservationDetails.date || externalEvents.length === 0) return;

    // If bookingTypeEvents are already set, skip (user already navigated dates)
    if (bookingTypeEvents.length > 0) return;

    // Skip for blocked/sold-out dates — they only show the unavailable message
    if (isDateBlocked(reservationDetails.date) || isDateSoldOut(reservationDetails.date)) return;

    const dateStr = reservationDetails.date;
    const blockingEvents = getBlockingEvents(dateStr);
    if (blockingEvents.length > 0) {
      setBookingTypeEvents(blockingEvents);
      setBookingTypeChoice('tickets');
      setSelectedTickets([]);
      setTicketTotal(0);
      if (blockingEvents.length === 1) {
        setSelectedEvent(blockingEvents[0]);
      } else {
        setSelectedEvent(null);
      }
      return;
    }

    const nonBlockingEvents = getNonBlockingEvents(dateStr);
    if (nonBlockingEvents.length > 0) {
      setBookingTypeEvents(nonBlockingEvents);
      setBookingTypeChoice(null);
    }
  }, [externalEvents, reservationDetails.date]);

  // Fetch shifts when date changes
  useEffect(() => {
    const fetchShifts = async () => {
      if (!reservationDetails.date || !restaurantId) {
        setShifts([]);
        setSlotAvailability({});
        return;
      }

      // Skip shift fetching for blocked or sold-out dates
      if (isDateBlocked(reservationDetails.date) || isDateSoldOut(reservationDetails.date)) {
        setShifts([]);
        setSlotAvailability({});
        return;
      }

      try {
        console.log('Fetching shifts for:', restaurantId, reservationDetails.date);
        const data = await getShiftsForDate(restaurantId, reservationDetails.date);
        console.log('Shifts response:', data);
        const fetchedShifts = data.shifts || [];
        setShifts(fetchedShifts);

        // Fetch slot availability for each shift
        if (fetchedShifts.length > 0) {
          const availabilityPromises = fetchedShifts.map(shift =>
            getSlotAvailability(shift.id, reservationDetails.date)
          );
          const availabilityResults = await Promise.all(availabilityPromises);

          // Create a map of shiftId -> availability data
          const availabilityMap = {};
          availabilityResults.forEach(result => {
            if (result && result.shiftId) {
              availabilityMap[result.shiftId] = result;
            }
          });
          setSlotAvailability(availabilityMap);
          console.log('Slot availability:', availabilityMap);
        } else {
          setSlotAvailability({});
        }
      } catch (err) {
        console.error('Error fetching shifts:', err);
        setShifts([]);
        setSlotAvailability({});
      }
    };
    fetchShifts();
  }, [restaurantId, reservationDetails.date]);

  // Clear selected time if it's no longer available (past time)
  useEffect(() => {
    if (reservationDetails.time && !timeSlots.includes(reservationDetails.time)) {
      handleStep1Change('time', '');
    }
  }, [reservationDetails.date, timeSlots]);

  // Note: blocked/sold-out dates are now clickable - they show a message instead of time slots

  // Clear selected time if guest count changes and slot no longer has enough capacity
  useEffect(() => {
    if (reservationDetails.time && Object.keys(slotAvailability).length > 0) {
      const slotInfo = getSlotInfo(reservationDetails.time);
      if (slotInfo.isBlocked || slotInfo.notEnoughCapacity) {
        handleStep1Change('time', '');
      }
    }
  }, [reservationDetails.guests, slotAvailability]);

  // Restore pending reservation after login
  useEffect(() => {
    if (isLoggedIn) {
      const pendingReservationStr = localStorage.getItem('pendingReservation');
      if (pendingReservationStr) {
        try {
          const pendingReservation = JSON.parse(pendingReservationStr);
          // Only restore if it's for the same restaurant
          if (pendingReservation.restaurantId === restaurantId) {
            setReservationDetails(pendingReservation.reservationDetails);
            setPersonalInfo(pendingReservation.personalInfo);
            if (pendingReservation.reservationDetails.specialOccasion) {
              setHasSpecialOccasion(true);
            }
            if (pendingReservation.selectedZoneId) {
              setSelectedZoneId(pendingReservation.selectedZoneId);
            }
            setStep(2); // Go to step 2
            // Clear the pending reservation
            localStorage.removeItem('pendingReservation');
          }
        } catch (err) {
          console.error('Failed to restore pending reservation:', err);
          localStorage.removeItem('pendingReservation');
        }
      }
    }
  }, [isLoggedIn, restaurantId]);

  // Auto-fill personal info for logged-in users
  useEffect(() => {
    if (isLoggedIn && user) {
      setPersonalInfo(prev => ({
        ...prev,
        firstName: user.firstName || prev.firstName,
        lastName: user.lastName || prev.lastName,
        email: user.email || prev.email,
        phone: user.phoneNumber || prev.phone,
      }));
    }
  }, [isLoggedIn, user]);


  const handleStep1Change = (field, value) => {
    setReservationDetails(prev => ({ ...prev, [field]: value }));

    // When date changes, check if new date has non-blocking events
    if (field === 'date') {
      // Skip booking type logic for blocked/sold-out dates — just show the message
      if (isDateBlocked(value) || isDateSoldOut(value)) {
        setBookingTypeChoice(null);
        setBookingTypeEvents([]);
      } else {
        const nonBlockingEvents = getNonBlockingEvents(value);
        if (nonBlockingEvents.length > 0) {
          // Date has non-blocking events - set them and reset choice to show selector
          setBookingTypeEvents(nonBlockingEvents);
          setBookingTypeChoice(null);
        } else {
          // No non-blocking events on this date - clear booking type state
          setBookingTypeChoice(null);
          setBookingTypeEvents([]);
        }
      }
    }
  };

  // Helper function to scroll to the selected time slot
  const scrollToSelectedSlot = (selectedTime) => {
    setTimeout(() => {
      if (timeSlotsContainerRef.current) {
        const slotButton = timeSlotsContainerRef.current.querySelector(`[data-time="${selectedTime}"]`);
        if (slotButton) {
          slotButton.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
      }
    }, 100);
  };

  // Handle time selection with auto-date-change for late-night slots (after midnight)
  const handleTimeSelection = (time) => {
    const newTimeIsLateNight = isNextDaySlot(time);
    const prevTimeIsLateNight = reservationDetails.time ? isNextDaySlot(reservationDetails.time) : false;

    // Only adjust date when transitioning between normal and late-night slots
    if (newTimeIsLateNight && !prevTimeIsLateNight && reservationDetails.date) {
      // Transitioning from normal slot (or no slot) to late-night slot: increment date
      const [y, m, d] = reservationDetails.date.split('-').map(Number);
      const currentDate = new Date(y, m - 1, d);
      currentDate.setDate(currentDate.getDate() + 1);
      const newDate = toLocalDateString(currentDate);

      setReservationDetails(prev => ({
        ...prev,
        date: newDate,
        time: time
      }));
      // Scroll to the selected slot after date change
      scrollToSelectedSlot(time);
    } else if (!newTimeIsLateNight && prevTimeIsLateNight && reservationDetails.date) {
      // Transitioning from late-night slot to normal slot: decrement date back
      const [y, m, d] = reservationDetails.date.split('-').map(Number);
      const currentDate = new Date(y, m - 1, d);
      currentDate.setDate(currentDate.getDate() - 1);
      const newDate = toLocalDateString(currentDate);

      setReservationDetails(prev => ({
        ...prev,
        date: newDate,
        time: time
      }));
      // Scroll to the selected slot after date change
      scrollToSelectedSlot(time);
    } else {
      // Same category (both normal or both late-night): just update time
      handleStep1Change('time', time);
    }
  };

  const handleStep2Change = (field, value) => {
    setPersonalInfo(prev => ({ ...prev, [field]: value }));
    if (field === 'email' && value !== verifiedEmail) {
      setEmailVerified(false);
      setEmailOtpSent(false);
      setEmailOtp('');
      setEmailOtpError('');
    }
  };

  // Email OTP handlers (event tickets only)
  const handleEmailOtpChange = (e, index) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 1) {
      const newOtp = emailOtp.split('');
      newOtp[index] = value;
      setEmailOtp(newOtp.join(''));
      if (value && index < 5) {
        const nextInput = document.getElementById(`ticket-otp-${index + 1}`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handleEmailOtpKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !emailOtp[index] && index > 0) {
      const prevInput = document.getElementById(`ticket-otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleEmailOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    setEmailOtp(pastedData);
  };

  const handleEmailOtpSubmit = async () => {
    if (!emailOtp || emailOtp.length !== 6) {
      setEmailOtpError('Please enter a valid 6-digit code');
      return;
    }
    setEmailOtpLoading(true);
    setEmailOtpError('');
    try {
      await verifyGuestOtp(personalInfo.email, emailOtp);
      setEmailVerified(true);
      setVerifiedEmail(personalInfo.email);
      setEmailOtpSent(false);
      setError('');
      // Auto-advance to step 3
      initializeTicketHolders();
      setTicketStep(3);
    } catch (err) {
      setEmailOtpError(err.response?.data?.message || 'Invalid code. Please try again.');
    } finally {
      setEmailOtpLoading(false);
    }
  };

  const handleEmailOtpResend = async () => {
    setEmailOtpLoading(true);
    setEmailOtpError('');
    try {
      await resendGuestOtp(personalInfo.email);
      setEmailOtp('');
    } catch (err) {
      setEmailOtpError(err.response?.data?.message || 'Failed to resend code.');
    } finally {
      setEmailOtpLoading(false);
    }
  };

  const scrollToTop = () => {
    // Scroll to absolute top of page
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    window.scrollTo(0, 0);
  };

  const validateStep1 = () => {
    if (!reservationDetails.date || !reservationDetails.time || !reservationDetails.guests) {
      setError('Please fill in all required fields');
      scrollToTop();
      return false;
    }
    // Check if selected date is blocked
    if (isDateBlocked(reservationDetails.date)) {
      setError('The selected date is not available for reservations. Please choose another date.');
      scrollToTop();
      return false;
    }
    // Check if selected date is sold out
    if (isDateSoldOut(reservationDetails.date)) {
      setError('The selected date is sold out. Please choose another date.');
      scrollToTop();
      return false;
    }
    setError('');
    return true;
  };

  const validateStep2 = () => {
    const { firstName, lastName, email, phone } = personalInfo;
    if (!firstName || !lastName || !email || !phone) {
      setError('Please fill in all required fields');
      scrollToTop();
      return false;
    }
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      scrollToTop();
      return false;
    }
    // Phone validation: must have at least 10 digits (including country code)
    const phoneDigits = phone.replace(/\D/g, '');
    if (phoneDigits.length < 10 || phoneDigits.length > 15) {
      setError('Please enter a valid phone number with country code');
      scrollToTop();
      return false;
    }
    setError('');
    return true;
  };

  const handleNext = () => {
    if (validateStep1()) {
      setStep(2);
      // Use setTimeout to scroll after React re-renders step 2
      setTimeout(() => {
        scrollToTop();
      }, 50);
    }
  };

  const handleLoginRedirect = () => {
    // Save current reservation details to localStorage so user can continue after login
    const reservationState = {
      restaurantId,
      restaurantName,
      reservationDetails,
      personalInfo,
      selectedZoneId,
    };
    localStorage.setItem('pendingReservation', JSON.stringify(reservationState));

    // Save current URL to return after login
    const returnUrl = window.location.pathname;
    localStorage.setItem('returnUrl', returnUrl);

    // Redirect to login page
    navigate('/login');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep2()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Determine if the selected time falls within a shift
      const selectedShift = reservationDetails.time ? getShiftForTime(reservationDetails.time) : null;

      const payload = {
        restaurantId,
        ...reservationDetails,
        ...personalInfo,
        guests: parseInt(reservationDetails.guests),
        // Include preferred zone if selected
        ...(selectedZoneId && {
          preferredZoneId: selectedZoneId,
        }),
        // Include shift ID if the selected time falls within a shift
        ...(selectedShift && {
          shiftId: selectedShift.id,
        }),
      };

      console.log('Reservation payload:', payload);
      console.log('Selected shift:', selectedShift);

      // Use appropriate API based on login status
      const response = isLoggedIn
        ? await createReservationAsUser(payload)
        : await createReservation(payload);

      // Check if automatic payment is required (redirect to payment page)
      if (response.requiresPayment && response.paymentUrl) {
        // Redirect to payment page with payment URL
        const params = new URLSearchParams({
          url: response.paymentUrl,
          reservationId: response.reservation.id.toString(),
          restaurantName: restaurantName,
        });
        navigate(`/payment?${params.toString()}`);
        return;
      }

      // Check if manual payment mode (admin will send payment link later)
      if (response.requiresPayment && response.paymentPending) {
        setSubmittedPaymentPending(true);
        setSuccess(true);
        setLoading(false);
        scrollToTop();
        return;
      }

      // Check if this went to waitlist
      if (response.isWaitlist) {
        setSubmittedToWaitlist(true);
        setWaitlistInfo({
          position: response.waitlistPosition,
          reason: response.waitlistReason,
        });
        setSuccess(true);
        setLoading(false);
        scrollToTop();
        return;
      }

      // Check if this was an approval-required slot
      const slotInfo = getSlotInfo(reservationDetails.time);
      setSubmittedRequiresApproval(slotInfo.requiresApproval || response.requiresApproval);

      // No payment required - show success
      setSuccess(true);
      setLoading(false);
      scrollToTop();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create reservation. Please try again.');
      setLoading(false);
      scrollToTop();
    }
  };

  const resetForm = () => {
    setStep(1);
    setSuccess(false);
    setSubmittedRequiresApproval(false);
    setSubmittedToWaitlist(false);
    setSubmittedPaymentPending(false);
    setWaitlistInfo({ position: null, reason: null });
    setReservationDetails({
      date: '',
      time: '',
      guests: '',
      specialOccasion: '',
      eventId: null,
    });
    setHasSpecialOccasion(false);
    setSelectedZoneId(null);
    setPersonalInfo({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      allergies: [],
      dietaryPreference: [],
      comments: '',
    });
    setError('');
    // Reset custom guest input state
    setShowCustomGuestInput(false);
    setCustomGuestValue('');
  };

  // Helper function to format waitlist reason
  const formatWaitlistReason = (reason) => {
    switch (reason) {
      case 'max_guests_per_reservation':
        return 'Large party reservations require special approval.';
      case 'max_covers':
        return 'This time slot has reached its guest capacity.';
      case 'max_reservations':
        return 'This time slot has reached its reservation limit.';
      default:
        return 'This time slot is currently at capacity.';
    }
  };

  // Ticket submission success screen
  if (ticketSubmitSuccess) {
    return (
      <div className="reservation-success">
        <div className="success-icon">
          {ticketAutoApproved ? (
            <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '64px' }}></i>
          ) : selectedEvent?.requiresApproval ? (
            <i className="bi bi-shield-check text-info" style={{ fontSize: '64px' }}></i>
          ) : (
            <i className="bi bi-ticket-perforated text-success" style={{ fontSize: '64px' }}></i>
          )}
        </div>
        <h3 className="mt-3">
          {ticketAutoApproved
            ? 'Tickets Confirmed!'
            : selectedEvent?.requiresApproval
              ? 'Ticket Request Submitted!'
              : 'Tickets Reserved Successfully!'}
        </h3>
        <p className="text-muted">
          {ticketAutoApproved
            ? <>Dear {personalInfo.firstName}, your tickets for <strong>{selectedEvent?.name}</strong> have been confirmed! Check your email for your ticket QR codes.</>
            : <>Dear {personalInfo.firstName}, your ticket reservation for <strong>{selectedEvent?.name}</strong> has been submitted.
              {selectedEvent?.requiresApproval
                ? ' Our team will review your request and send you a payment link via email once approved.'
                : ' You will receive an email with further instructions.'}</>}
        </p>
        <div className="ticket-success-info-box">
          <i className="bi bi-envelope me-2"></i>
          <span>
            {ticketAutoApproved
              ? 'Your ticket QR codes have been sent to your email.'
              : selectedEvent?.requiresApproval
                ? 'Check your email for approval status and payment link.'
                : 'A confirmation email has been sent to your inbox.'}
          </span>
        </div>
        <button className="btn btn-primary mt-3" onClick={exitTicketMode}>
          Back to Reservations
        </button>
      </div>
    );
  }

  // Ticket mode UI
  if (ticketMode && selectedEvent) {
    return (
      <div className="reservation-form-container" ref={formContainerRef}>
        <div className="form-header">
          <h3>
            <i className="bi bi-ticket-perforated me-2"></i>
            Get Tickets
          </h3>
          <div className="step-indicator">
            <span className={ticketStep === 1 ? 'active' : ticketStep > 1 ? 'completed' : ''}>1</span>
            <div className="step-line"></div>
            <span className={ticketStep === 2 ? 'active' : ticketStep > 2 ? 'completed' : ''}>2</span>
            <div className="step-line"></div>
            <span className={ticketStep === 3 ? 'active' : ''}>3</span>
          </div>
        </div>

        {error && (
          <div className="error-alert" role="alert">
            {error}
          </div>
        )}

        {/* Step 1: Select Tickets */}
        {ticketStep === 1 && (
          <div className="step-content">
            <h4 className="step-title">Step 1: Select Tickets</h4>
            <EventTicketSelector
              event={selectedEvent}
              onSelectionChange={handleTicketSelectionChange}
              onBack={exitTicketMode}
              restaurantName={restaurantName}
            />

            {selectedTickets.length > 0 && (
              <button
                type="button"
                className="btn btn-next mt-4"
                onClick={() => {
                  setTicketStep(2);
                }}
              >
                Continue to Your Details
              </button>
            )}
          </div>
        )}

        {/* Step 2: Buyer Information */}
        {ticketStep === 2 && (
          <div className="step-content step-2-content">
            <h4 className="step-title">Step 2: Your Information</h4>
            <p className="form-hint text-muted mb-3">
              We'll send your payment link and tickets to this email.
            </p>

            <div className="form-section">
              {/* Email — full width, first field, prominent */}
              <div className="ticket-email-field-wrapper mb-3">
                <label htmlFor="buyerEmail" className="form-label">
                  <i className="bi bi-envelope"></i> Email Address *
                </label>
                <div className="ticket-email-input-row">
                  <input
                    type="email"
                    className={`form-control modern-input ${emailVerified && personalInfo.email === verifiedEmail ? 'ticket-email-input-verified' : ''}`}
                    id="buyerEmail"
                    value={personalInfo.email}
                    onChange={(e) => handleStep2Change('email', e.target.value)}
                    placeholder="you@example.com"
                    required
                    readOnly={emailVerified && personalInfo.email === verifiedEmail}
                  />
                  {emailVerified && personalInfo.email === verifiedEmail && (
                    <span className="ticket-email-verified-badge">
                      <i className="bi bi-check-circle-fill"></i> Verified
                    </span>
                  )}
                </div>

                {/* OTP inline section */}
                {emailOtpSent && !emailVerified && (
                  <div className="ticket-otp-section">
                    <div className="ticket-otp-sent-to">
                      <i className="bi bi-envelope-check"></i>
                      <span>We sent a 6-digit code to <strong>{personalInfo.email}</strong></span>
                    </div>

                    <div className="ticket-otp-inputs">
                      {[0, 1, 2, 3, 4, 5].map((index) => (
                        <input
                          key={index}
                          id={`ticket-otp-${index}`}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={emailOtp[index] || ''}
                          onChange={(e) => handleEmailOtpChange(e, index)}
                          onKeyDown={(e) => handleEmailOtpKeyDown(e, index)}
                          onPaste={index === 0 ? handleEmailOtpPaste : undefined}
                          className="ticket-otp-input"
                          autoFocus={index === 0}
                        />
                      ))}
                    </div>

                    {emailOtpError && (
                      <div className="ticket-otp-error">
                        <i className="bi bi-exclamation-circle"></i> {emailOtpError}
                      </div>
                    )}

                    <div className="ticket-otp-actions">
                      <button
                        type="button"
                        className="btn btn-next btn-primary-submit ticket-otp-verify-btn"
                        onClick={handleEmailOtpSubmit}
                        disabled={emailOtpLoading || emailOtp.length !== 6}
                      >
                        {emailOtpLoading ? (
                          <><span className="ticket-otp-spinner"></span> Verifying...</>
                        ) : (
                          <><i className="bi bi-shield-check"></i> Verify Email</>
                        )}
                      </button>
                      <button
                        type="button"
                        className="ticket-otp-resend-link"
                        onClick={handleEmailOtpResend}
                        disabled={emailOtpLoading}
                      >
                        Didn't receive it? <span>Resend Code</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Name row */}
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label htmlFor="buyerFirstName" className="form-label">First Name *</label>
                  <input
                    type="text"
                    className="form-control modern-input"
                    id="buyerFirstName"
                    value={personalInfo.firstName}
                    onChange={(e) => handleStep2Change('firstName', e.target.value)}
                    placeholder="First Name"
                    required
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label htmlFor="buyerLastName" className="form-label">Last Name *</label>
                  <input
                    type="text"
                    className="form-control modern-input"
                    id="buyerLastName"
                    value={personalInfo.lastName}
                    onChange={(e) => handleStep2Change('lastName', e.target.value)}
                    placeholder="Last Name"
                    required
                  />
                </div>
              </div>

              {/* Phone */}
              <div className="mb-3">
                <label htmlFor="buyerPhone" className="form-label">Phone Number *</label>
                <PhoneInput
                  country={'eg'}
                  value={personalInfo.phone}
                  onChange={(phone) => handleStep2Change('phone', '+' + phone)}
                  inputProps={{ id: 'buyerPhone', required: true }}
                  containerClass="phone-input-container"
                  inputClass="form-control modern-input"
                  buttonClass="phone-input-button"
                  enableSearch={true}
                  searchPlaceholder="Search country..."
                  preferredCountries={['eg', 'sa', 'ae', 'us', 'gb']}
                />
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-back"
                onClick={() => {
                  setTicketStep(1);
                }}
              >
                Back
              </button>
              <button
                type="button"
                className="btn btn-next btn-primary-submit"
                onClick={async () => {
                  if (!personalInfo.firstName || !personalInfo.lastName || !personalInfo.email || !personalInfo.phone) {
                    setError('Please fill in all required fields');
                    scrollToTop();
                    return;
                  }
                  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  if (!emailRegex.test(personalInfo.email)) {
                    setError('Please enter a valid email address');
                    scrollToTop();
                    return;
                  }
                  setError('');
                  // If already verified with same email, proceed
                  if (emailVerified && personalInfo.email === verifiedEmail) {
                    initializeTicketHolders();
                    setTicketStep(3);
                    return;
                  }
                  // Otherwise, send OTP
                  setEmailOtpLoading(true);
                  setEmailOtpError('');
                  try {
                    await sendGuestOtp(personalInfo.email);
                    setEmailOtpSent(true);
                    setEmailOtp('');
                  } catch (err) {
                    setEmailOtpError(err.response?.data?.message || 'Failed to send verification code.');
                  } finally {
                    setEmailOtpLoading(false);
                  }
                }}
                disabled={emailOtpLoading}
              >
                {emailOtpLoading ? 'Sending...' : 'Continue to Ticket Holders'}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Ticket Holder Information */}
        {ticketStep === 3 && (
          <div className="step-content step-3-content">
            <h4 className="step-title">Step 3: Ticket Holder Details</h4>
            <p className="form-hint text-muted mb-3">
              Please provide details for each ticket holder. Each person will receive their own QR code.
            </p>

            {ticketHolders.map((holder, index) => (
              <div key={index} className="ticket-holder-card">
                <div className="ticket-holder-header">
                  <h5>
                    <i className="bi bi-ticket-perforated"></i>
                    Ticket {index + 1}: {holder.ticketTypeName}
                  </h5>
                  {index === 0 && (
                    <div className="same-as-buyer-check">
                      <input
                        type="checkbox"
                        id={`sameAsBuyer-${index}`}
                        checked={holder.sameAsBuyer}
                        onChange={(e) => handleTicketHolderChange(index, 'sameAsBuyer', e.target.checked)}
                      />
                      <label htmlFor={`sameAsBuyer-${index}`}>Same as buyer</label>
                    </div>
                  )}
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">First Name *</label>
                    <input
                      type="text"
                      className="form-control modern-input"
                      value={holder.firstName}
                      onChange={(e) => handleTicketHolderChange(index, 'firstName', e.target.value)}
                      placeholder="First Name"
                      disabled={holder.sameAsBuyer}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Last Name *</label>
                    <input
                      type="text"
                      className="form-control modern-input"
                      value={holder.lastName}
                      onChange={(e) => handleTicketHolderChange(index, 'lastName', e.target.value)}
                      placeholder="Last Name"
                      disabled={holder.sameAsBuyer}
                      required
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      className="form-control modern-input"
                      value={holder.email}
                      onChange={(e) => handleTicketHolderChange(index, 'email', e.target.value)}
                      placeholder="Email Address"
                      disabled={holder.sameAsBuyer}
                      required
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Phone *</label>
                    <input
                      type="tel"
                      className="form-control modern-input"
                      value={holder.phone}
                      onChange={(e) => handleTicketHolderChange(index, 'phone', e.target.value)}
                      placeholder="Phone Number"
                      disabled={holder.sameAsBuyer}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-12 mb-3">
                    <label className="form-label">
                      <i className="bi bi-link-45deg me-1"></i>
                      Social Media Link *
                    </label>
                    <input
                      type="url"
                      className="form-control modern-input"
                      value={holder.socialMediaLink}
                      onChange={(e) => handleTicketHolderChange(index, 'socialMediaLink', e.target.value)}
                      placeholder="Instagram, Facebook, or other profile link"
                      required
                    />
                  </div>
                </div>
              </div>
            ))}

            {/* Order Summary */}
            <div className="ticket-order-summary">
              <h5>Order Summary</h5>
              {selectedTickets.map(ticket => (
                <div key={ticket.ticketTypeId} className="summary-item">
                  <span>{ticket.quantity}x {ticket.ticketTypeName}</span>
                  <span>EGP {((ticket.priceCents / 100) * ticket.quantity * (ticket.priceExcludesVat ? 1.14 : 1)).toFixed(2)}</span>
                </div>
              ))}
              {(() => {
                const subtotalCents = selectedTickets.reduce((sum, t) => sum + t.priceCents * t.quantity, 0);
                const totalTicketCount = selectedTickets.reduce((sum, t) => sum + t.quantity, 0);
                const feeCents = ticketFeeConfig.fixedFeeCents * totalTicketCount + Math.round(subtotalCents * ticketFeeConfig.feePercent / 100);
                const hasFee = feeCents > 0;
                return hasFee ? (
                  <>
                    <div className="summary-item fee-line">
                      <span>Processing Fee</span>
                      <span>EGP {(feeCents / 100).toFixed(2)}</span>
                    </div>
                    <div className="summary-total">
                      <span>Total</span>
                      <span>EGP {(ticketTotal + feeCents / 100).toFixed(2)}</span>
                    </div>
                  </>
                ) : (
                  <div className="summary-total">
                    <span>Total</span>
                    <span>EGP {ticketTotal.toFixed(2)}</span>
                  </div>
                );
              })()}
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-back"
                onClick={() => {
                  setTicketStep(2);
                }}
              >
                Back
              </button>
              <button
                type="button"
                className="btn btn-submit btn-primary-submit"
                onClick={() => {
                  // Validate all ticket holders
                  const invalid = ticketHolders.some(h => !h.firstName || !h.lastName || !h.email || !h.socialMediaLink);
                  if (invalid) {
                    setError('Please fill in all required fields for each ticket holder');
                    scrollToTop();
                    return;
                  }
                  setError('');
                  handleTicketSubmit();
                }}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Submitting...
                  </>
                ) : (
                  'Submit Ticket Reservation'
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (success) {
    return (
      <div className="reservation-success">
        <div className="success-icon">
          {submittedToWaitlist ? (
            <i className="bi bi-hourglass-split text-warning" style={{ fontSize: '64px' }}></i>
          ) : submittedRequiresApproval ? (
            <i className="bi bi-shield-check text-info" style={{ fontSize: '64px' }}></i>
          ) : submittedPaymentPending ? (
            <i className="bi bi-credit-card text-primary" style={{ fontSize: '64px' }}></i>
          ) : (
            <i className="bi bi-check-circle-fill text-success" style={{ fontSize: '64px' }}></i>
          )}
        </div>
        {submittedToWaitlist ? (
          <>
            <h3 className="mt-3">You're on the Waitlist!</h3>
            <div className="waitlist-position-badge">
              #{waitlistInfo.position}
            </div>
            <p className="text-muted">
              Dear {personalInfo.firstName}, your reservation has been added to the waitlist.
              {formatWaitlistReason(waitlistInfo.reason)}
            </p>
            <div className="waitlist-info-box">
              <i className="bi bi-info-circle me-2"></i>
              <span>We'll notify you by email as soon as a spot becomes available. If approved, you'll receive instructions to complete your booking.</span>
            </div>
          </>
        ) : submittedRequiresApproval ? (
          <>
            <h3 className="mt-3">Reservation Request Submitted!</h3>
            <p className="text-muted">
              Dear {personalInfo.firstName}, your reservation request has been submitted for review.
              The restaurant team will review your request and get back to you via email shortly.
            </p>
            <div className="approval-info-box">
              <i className="bi bi-info-circle me-2"></i>
              <span>This time slot requires approval. You will receive a confirmation email once your request is approved.</span>
            </div>
          </>
        ) : submittedPaymentPending ? (
          <>
            <h3 className="mt-3">Reservation Submitted!</h3>
            <p className="text-muted">
              Dear {personalInfo.firstName}, your reservation has been submitted successfully.
              Our team will contact you regarding payment details.
            </p>
            <div className="payment-pending-info-box">
              <i className="bi bi-credit-card me-2"></i>
              <span>A payment link will be sent to your email or phone. Your reservation will be confirmed once payment is complete.</span>
            </div>
          </>
        ) : (
          <>
            <h3 className="mt-3">Reservation Request Successful!</h3>
            <p className="text-muted">
              Dear {personalInfo.firstName}, your request is successful! Someone from the team will get back to you via email or WhatsApp.
            </p>
          </>
        )}
        <button className="btn btn-primary mt-3" onClick={resetForm}>
          Make Another Reservation
        </button>
      </div>
    );
  }

  return (
    <div className="reservation-form-container" ref={formContainerRef}>
      {/* Event Popup - Show when user clicks on an event */}
      {showEventPopup && externalEvents.length > 0 && (
        <EventPopup
          events={externalEvents}
          initialIndex={selectedEventIndex}
          onClose={() => setShowEventPopup(false)}
        />
      )}

      <div className="form-header">
        <h3>
          <i className="bi bi-calendar-check me-2"></i>
          Reserve a Table at {restaurantName}
        </h3>
        <div className="step-indicator">
          <span className={step === 1 ? 'active' : step > 1 ? 'completed' : ''}>1</span>
          <div className="step-line"></div>
          <span className={step === 2 ? 'active' : ''}>2</span>
        </div>
      </div>

      {error && (
        <div className="error-alert" role="alert">
          {error}
        </div>
      )}

      {step === 1 && (
        <div className="step-content step-1-content">
          <h4 className="step-title">Step 1: Reservation Details</h4>

          {/* Date Selection */}
          <div className="form-section">
            <div className="section-header">
              <label className="form-label">Select a date:</label>
              <div className="view-toggle">
                <button
                  type="button"
                  className={`toggle-btn ${dateViewMode === 'quick' ? 'active' : ''}`}
                  onClick={() => setDateViewMode('quick')}
                >
                  Quick Date
                </button>
                <button
                  type="button"
                  className={`toggle-btn ${dateViewMode === 'calendar' ? 'active' : ''}`}
                  onClick={() => setDateViewMode('calendar')}
                >
                  Calendar View
                </button>
              </div>
            </div>

            {/* Blocked Dates Message */}
            {allQuickDatesBlocked() && nextAvailableDate && (
              <div className="blocked-dates-message">
                <i className="bi bi-calendar-x me-2"></i>
                <span>
                  We will start accepting reservations from <strong>{new Date(nextAvailableDate).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</strong>. Please use the calendar view to select a date.
                </span>
              </div>
            )}

            {dateViewMode === 'quick' ? (
              <div className="quick-dates-scroll">
                <button className="scroll-btn left" onClick={(e) => {
                  e.currentTarget.nextElementSibling.scrollBy({ left: -200, behavior: 'smooth' });
                }}>
                  <i className="bi bi-chevron-left"></i>
                </button>
                <div className="quick-dates-container" ref={quickDatesContainerRef}>
                  {getQuickDates().map((d, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className={`quick-date-pill ${reservationDetails.date === d.value ? 'active' : ''} ${d.isBlocked ? 'blocked' : ''} ${d.isSoldOut && !d.isBlocked ? 'sold-out' : ''} ${d.hasEvent ? 'has-event' : ''}`}
                      onClick={() => {
                        if (d.hasEvent) {
                          handleEventBadgeClick({ stopPropagation: () => {} }, d.value);
                        } else {
                          handleStep1Change('date', d.value);
                        }
                      }}
                      title={d.isBlocked ? 'This date is not available for reservations' : d.isSoldOut ? 'Sold out - no availability' : ''}
                    >
                      {d.hasEvent && (
                        <div className="event-badge" onClick={(e) => handleEventBadgeClick(e, d.value)}>
                          <i className="bi bi-calendar-event-fill"></i>
                          <span>Event</span>
                        </div>
                      )}
                      <div className="weekday">{d.weekday}</div>
                      <div className="date">{d.monthDay}</div>
                      {d.isBlocked && <i className="bi bi-lock-fill blocked-icon"></i>}
                      {d.isSoldOut && !d.isBlocked && (
                        <div className="sold-out-badge">
                          <i className="bi bi-x-circle-fill"></i>
                          <span>SOLD OUT</span>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
                <button className="scroll-btn right" onClick={(e) => {
                  e.currentTarget.previousElementSibling.scrollBy({ left: 200, behavior: 'smooth' });
                }}>
                  <i className="bi bi-chevron-right"></i>
                </button>
              </div>
            ) : (
              <div className="calendar-view">
                <input
                  type="date"
                  className="form-control calendar-input"
                  value={reservationDetails.date}
                  onChange={(e) => {
                    const selectedDate = e.target.value;
                    const dateObj = new Date(selectedDate + 'T00:00:00');
                    const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'long' });
                    const formattedDate = dateObj.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
                    if (isDateBlocked(selectedDate)) {
                      setCalendarToast({ type: 'blocked', message: 'This date is closed for reservations.', dayName, formattedDate });
                      e.target.value = reservationDetails.date || '';
                      return;
                    }
                    if (isDateSoldOut(selectedDate)) {
                      setCalendarToast({ type: 'soldout', message: 'This date is sold out.', dayName, formattedDate });
                      e.target.value = reservationDetails.date || '';
                      return;
                    }
                    if (hasBlockingEvent(selectedDate)) {
                      setCalendarToast({ type: 'event', message: 'This date has a special event. Use Quick Dates to access tickets.', dayName, formattedDate });
                      e.target.value = reservationDetails.date || '';
                      return;
                    }
                    setCalendarToast(null);
                    setError('');
                    handleStep1Change('date', selectedDate);
                  }}
                  min={toLocalDateString(new Date())}
                />
                {calendarToast && (
                  <div className="calendar-popup-overlay" onClick={() => setCalendarToast(null)}>
                    <div className={`calendar-popup calendar-popup-${calendarToast.type}`} onClick={(e) => e.stopPropagation()}>
                      <div className="calendar-popup-icon">
                        <i className={`bi ${calendarToast.type === 'soldout' ? 'bi-bag-x-fill' : calendarToast.type === 'event' ? 'bi-calendar-event-fill' : 'bi-lock-fill'}`}></i>
                      </div>
                      <div className="calendar-popup-date">
                        {calendarToast.dayName}, {calendarToast.formattedDate}
                      </div>
                      <div className="calendar-popup-message">{calendarToast.message}</div>
                      <button className="calendar-popup-btn" onClick={() => setCalendarToast(null)}>
                        OK
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Booking Type Selection - Show when date has events */}
          {reservationDetails.date && bookingTypeEvents.length > 0 && !isDateSoldOut(reservationDetails.date) && (
            <div className="form-section booking-type-section">
              {/* Check if all events block regular reservations */}
              {bookingTypeEvents.some(e => e.blocksRegularReservations) ? (
                <>
                  <label className="form-label">
                    <i className="bi bi-ticket-perforated me-2"></i>
                    Special Event
                  </label>
                  <p className="form-hint text-muted">
                    This date has a special event. Regular reservations are not available.
                  </p>
                </>
              ) : (
                <>
                  <label className="form-label">
                    <i className="bi bi-calendar2-check me-2"></i>
                    Choose Booking Type
                  </label>
                  <p className="form-hint text-muted">
                    This date has a special event. How would you like to proceed?
                  </p>
                </>
              )}

              <div className="booking-type-options">
                {/* Only show Regular Reservation if no events block it */}
                {!bookingTypeEvents.some(e => e.blocksRegularReservations) && (
                  <button
                    type="button"
                    className={`booking-type-option ${bookingTypeChoice === 'regular' ? 'active' : ''}`}
                    onClick={handleSelectRegularReservation}
                  >
                    <div className="booking-type-option-icon">
                      <i className="bi bi-table"></i>
                    </div>
                    <div className="booking-type-option-content">
                      <h4>Regular Reservation</h4>
                      <p>Book a table for dining</p>
                    </div>
                    {bookingTypeChoice === 'regular' && (
                      <i className="bi bi-check-circle-fill booking-type-check"></i>
                    )}
                  </button>
                )}

                <button
                  type="button"
                  className={`booking-type-option booking-type-option-event ${bookingTypeChoice === 'tickets' ? 'active' : ''}`}
                  onClick={() => {
                    if (bookingTypeEvents.length === 1) {
                      handleSelectEventTickets(bookingTypeEvents[0]);
                    } else {
                      // For multiple events, just set choice to 'tickets' to show event selection
                      setBookingTypeChoice('tickets');
                    }
                  }}
                >
                  <div className="booking-type-option-icon">
                    <i className="bi bi-ticket-perforated"></i>
                  </div>
                  <div className="booking-type-option-content">
                    <h4>Event Tickets</h4>
                    <p>{bookingTypeEvents.length === 1 ? bookingTypeEvents[0].title : `${bookingTypeEvents.length} events available`}</p>
                  </div>
                  {bookingTypeChoice === 'tickets' && (
                    <i className="bi bi-check-circle-fill booking-type-check"></i>
                  )}
                </button>
              </div>

              {/* Event Selection - Show when tickets selected and multiple events exist */}
              {bookingTypeChoice === 'tickets' && bookingTypeEvents.length > 1 && !selectedEvent && (
                <div className="booking-type-events-list mt-3">
                  <p className="form-hint text-muted mb-2">Select an event:</p>
                  {bookingTypeEvents.map((event) => (
                    <button
                      key={event.id}
                      type="button"
                      className={`booking-type-event-item ${selectedEvent?.id === event.id ? 'active' : ''}`}
                      onClick={() => handleSelectEventTickets(event)}
                    >
                      {event.imageUrl && (
                        <img
                          src={event.imageUrl}
                          alt={event.title}
                          className="booking-type-event-thumb"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      )}
                      <div className="booking-type-event-info">
                        <h5>{event.title}</h5>
                        {event.time && <span className="booking-type-event-time"><i className="bi bi-clock me-1"></i>{event.time}</span>}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Inline Event Ticket Selector - Show when user chose event tickets */}
              {bookingTypeChoice === 'tickets' && selectedEvent && (
                <div className="inline-ticket-selector">
                  {bookingTypeEvents.length > 1 && (
                    <button
                      type="button"
                      className="btn btn-link p-0 mb-2 text-decoration-none"
                      onClick={() => {
                        setSelectedEvent(null);
                        setSelectedTickets([]);
                        setTicketTotal(0);
                      }}
                    >
                      <i className="bi bi-arrow-left me-1"></i>Choose a different event
                    </button>
                  )}
                  <EventTicketSelector
                    event={selectedEvent}
                    onSelectionChange={handleTicketSelectionChange}
                    restaurantName={restaurantName}
                  />

                  {selectedTickets.length > 0 && (
                    <button
                      type="button"
                      className="btn btn-next mt-3"
                      onClick={() => {
                        // Enter full ticket mode for buyer info
                        setTicketMode(true);
                        setTicketStep(2);
                      }}
                    >
                      Continue to Your Details
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Sold Out / Blocked Date Message — replaces time slots */}
          {reservationDetails.date && isDateSoldOut(reservationDetails.date) && !isDateBlocked(reservationDetails.date) && (
            <div className="form-section">
              <div className="date-unavailable-message sold-out">
                <i className="bi bi-bag-x-fill"></i>
                <div>
                  <strong>Sold Out</strong>
                  <p>This date is fully booked. Please select another date.</p>
                </div>
              </div>
            </div>
          )}

          {reservationDetails.date && isDateBlocked(reservationDetails.date) && (
            <div className="form-section">
              <div className="date-unavailable-message blocked">
                <i className="bi bi-calendar-x-fill"></i>
                <div>
                  <strong>Closed</strong>
                  <p>We are closed on this date. Please select another date.</p>
                </div>
              </div>
            </div>
          )}

          {/* Time Selection - Only show when no event choice needed OR user chose regular reservation */}
          {reservationDetails.date && !isDateBlocked(reservationDetails.date) && !isDateSoldOut(reservationDetails.date) && (bookingTypeEvents.length === 0 || bookingTypeChoice === 'regular') && (
          <div className="form-section">
            <label className="form-label">Select a time:</label>
            <p className="form-hint text-muted">
              {reservationDetails.date ? 'Select your preferred time slot' : 'Please select a date first'}
            </p>

            {/* Visible Shifts Selection - Show shift pills when shifts have showLabelToCustomers enabled */}
            {visibleShifts.length > 0 && reservationDetails.date && (
              <div className="shifts-selection mb-3">
                <div className="shifts-pills-container">
                  {visibleShifts.map(shift => (
                    <button
                      key={shift.id}
                      type="button"
                      className={`shift-pill ${
                        reservationDetails.time &&
                        getShiftLabelForTime(reservationDetails.time) === shift.label
                          ? 'active'
                          : ''
                      }`}
                      onClick={() => {
                        // Select the first available time slot in this shift
                        const firstSlotInShift = timeSlots.find(time => getShiftLabelForTime(time) === shift.label);
                        if (firstSlotInShift) {
                          handleStep1Change('time', firstSlotInShift);
                          // Scroll the time slots container to show the first slot of this shift
                          setTimeout(() => {
                            if (timeSlotsContainerRef.current) {
                              const slotIndex = timeSlots.indexOf(firstSlotInShift);
                              const slotButtons = timeSlotsContainerRef.current.querySelectorAll('.time-pill');
                              if (slotButtons[slotIndex]) {
                                slotButtons[slotIndex].scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
                              }
                            }
                          }, 50);
                        }
                      }}
                    >
                      <span className="shift-label">{shift.label}</span>
                      <span className="shift-time">
                        {formatTime(shift.shiftStartTime)} - {formatTime(shift.shiftEndTime)}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Time Slots */}
            <div className="time-slots-scroll">
              <button className="scroll-btn left" onClick={(e) => {
                e.currentTarget.nextElementSibling.scrollBy({ left: -200, behavior: 'smooth' });
              }}>
                <i className="bi bi-chevron-left"></i>
              </button>
              <div className="time-slots-container" ref={timeSlotsContainerRef}>
                {timeSlots && timeSlots.length > 0 ? (
                  timeSlots.map(time => {
                    const shiftLabel = getShiftLabelForTime(time);
                    const slotInfo = getSlotInfo(time);
                    const isBlocked = slotInfo.isBlocked;
                    // Waitlist slot: over capacity with capacity enforcement ON (allowOverbooking=false)
                    const isWaitlistSlot = slotInfo.isOverCapacity && !slotInfo.allowOverbooking;
                    const requiresApproval = slotInfo.requiresApproval;
                    // Waitlist slots are NOT unavailable - only manually blocked slots are
                    const isUnavailable = isBlocked;

                    // Determine which capacity to display:
                    // - If slot has a capacity range (maxCapacity differs from shiftMaxCapacity), use remainingCapacity (range-based)
                    // - If no capacity range, use shiftRemainingCovers (shift-wide)
                    const hasCapacityRange = slotInfo.maxCapacity !== null && slotInfo.shiftMaxCapacity !== null && slotInfo.maxCapacity !== slotInfo.shiftMaxCapacity;
                    const displayCapacity = hasCapacityRange ? slotInfo.remainingCapacity : slotInfo.shiftRemainingCovers;
                    const showLowCapacity = !isUnavailable && !isWaitlistSlot && displayCapacity !== null && displayCapacity > 0 && displayCapacity <= 30;

                    // Determine tooltip message
                    let tooltipMsg = shiftLabel || '';
                    if (isBlocked) {
                      tooltipMsg = 'This slot is not available';
                    } else if (requiresApproval) {
                      tooltipMsg = 'This slot requires approval';
                    } else if (isWaitlistSlot) {
                      tooltipMsg = 'You will be added to the waitlist';
                    } else if (displayCapacity !== null) {
                      tooltipMsg = `${displayCapacity} spots available`;
                    }

                    return (
                      <button
                        key={time}
                        type="button"
                        data-time={time}
                        className={`time-pill ${reservationDetails.time === time ? 'active' : ''} ${shiftLabel ? 'has-shift' : ''} ${isBlocked ? 'blocked' : ''} ${isWaitlistSlot ? 'waitlist' : ''} ${requiresApproval ? 'requires-approval' : ''}`}
                        onClick={() => !isUnavailable && handleTimeSelection(time)}
                        disabled={isUnavailable}
                        title={tooltipMsg}
                      >
                        {formatTime(time)}
                        {isBlocked && (
                          <i className="bi bi-lock-fill blocked-icon"></i>
                        )}
                        {requiresApproval && !isBlocked && (
                          <span className="approval-indicator">
                            <i className="bi bi-shield-check"></i>
                          </span>
                        )}
                        {isWaitlistSlot && (
                          <span className="waitlist-indicator">
                            <i className="bi bi-hourglass-split"></i> Waitlist
                          </span>
                        )}
                        {shiftLabel && visibleShifts.length > 0 && !isUnavailable && (
                          <span></span>
                        )}
                      </button>
                    );
                  })
                ) : (
                  <p className="text-muted">No time slots available</p>
                )}
              </div>
              <button className="scroll-btn right" onClick={(e) => {
                e.currentTarget.previousElementSibling.scrollBy({ left: 200, behavior: 'smooth' });
              }}>
                <i className="bi bi-chevron-right"></i>
              </button>
            </div>



            {/* Show approval hint when slot requires approval */}
            {reservationDetails.time && getSlotInfo(reservationDetails.time).requiresApproval && (
              <div className="approval-hint mt-2">
                <i className="bi bi-shield-check me-2"></i>
                <span>This time slot requires approval. You will receive a confirmation once your request is reviewed.</span>
              </div>
            )}
          </div>
          )}

          {/* Table for - Only show when no event choice needed OR user chose regular reservation, and date is not blocked/sold-out */}
          {(!reservationDetails.date || (!isDateBlocked(reservationDetails.date) && !isDateSoldOut(reservationDetails.date))) && (bookingTypeEvents.length === 0 || bookingTypeChoice === 'regular') && (
          <div className="form-section">
            <label className="form-label">Table for</label>
            <div className="guests-scroll">
              <button className="scroll-btn left" onClick={(e) => {
                e.currentTarget.nextElementSibling.scrollBy({ left: -200, behavior: 'smooth' });
              }}>
                <i className="bi bi-chevron-left"></i>
              </button>
              <div className="guests-container">
                {Array.from({ length: maxGuestsForPills }, (_, i) => i + 1).map(num => (
                  <button
                    key={num}
                    type="button"
                    className={`guest-pill ${reservationDetails.guests === num && !showCustomGuestInput ? 'active' : ''}`}
                    onClick={() => {
                      handleStep1Change('guests', num);
                      setShowCustomGuestInput(false);
                      setCustomGuestValue('');
                    }}
                  >
                    {num}
                  </button>
                ))}
                <button
                  type="button"
                  className={`guest-pill more-guests ${showCustomGuestInput ? 'active' : ''}`}
                  onClick={() => {
                    setShowCustomGuestInput(!showCustomGuestInput);
                    if (!showCustomGuestInput) {
                      setCustomGuestValue(reservationDetails.guests > maxGuestsForPills ? reservationDetails.guests.toString() : '');
                    }
                  }}
                  title="Enter a larger number"
                >
                  {showCustomGuestInput ? <i className="bi bi-x-lg"></i> : <i className="bi bi-plus-lg"></i>}
                </button>
              </div>
              <button className="scroll-btn right" onClick={(e) => {
                e.currentTarget.previousElementSibling.scrollBy({ left: 200, behavior: 'smooth' });
              }}>
                <i className="bi bi-chevron-right"></i>
              </button>
            </div>

            {/* Custom guest input below pills */}
            {showCustomGuestInput && (
              <div className="custom-guest-below">
                <div className="custom-guest-input-row">
                  <i className="bi bi-people-fill"></i>
                  <input
                    type="number"
                    className="custom-guest-input-field"
                    value={customGuestValue}
                    onChange={(e) => {
                      const val = e.target.value;
                      setCustomGuestValue(val);
                      const num = parseInt(val);
                      if (num >= 1 && num <= 250) {
                        handleStep1Change('guests', num);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    min="1"
                    max="250"
                    placeholder={`${maxGuestsForPills + 1}+ guests`}
                    autoFocus
                  />
                </div>
                {customGuestValue && (parseInt(customGuestValue) < 1 || parseInt(customGuestValue) > 250) && (
                  <small className="text-danger mt-2 d-block">
                    <i className="bi bi-exclamation-circle me-1"></i>
                    Please enter a number between 1 and 250
                  </small>
                )}
              </div>
            )}

            {/* Waitlist hint for large party */}
            {reservationDetails.time && (() => {
              const slotInfo = getSlotInfo(reservationDetails.time);
              const maxAllowedGuests = slotInfo.maxAllowedGuestsNumber;
              const willBeWaitlisted = maxAllowedGuests && reservationDetails.guests > maxAllowedGuests;
              const shiftAtCapacity = slotInfo.shiftRemainingCovers !== null && slotInfo.shiftRemainingCovers < reservationDetails.guests;

              if (willBeWaitlisted) {
                return (
                  <div className="waitlist-hint mt-2">
                    <i className="bi bi-info-circle me-2"></i>
                    <span>
                      Reservations for more than {maxAllowedGuests} guests require approval.
                      You will be placed on the waitlist.
                    </span>
                  </div>
                );
              }

              if (shiftAtCapacity && !slotInfo.allowOverbooking) {
                return (
                  <div className="waitlist-hint mt-2">
                    <i className="bi bi-info-circle me-2"></i>
                    <span>
                      This time slot has limited availability. Your booking may be placed on the waitlist.
                    </span>
                  </div>
                );
              }

              return null;
            })()}

            {/* Show payment info for automatic payment mode with a payment group */}
            {reservationDetails.time && reservationDetails.guests &&
              getShiftForTime(reservationDetails.time)?.paymentMode === 'automatic' &&
              getShiftForTime(reservationDetails.time)?.defaultPaymentGroup && (() => {
              const shift = getShiftForTime(reservationDetails.time);
              const paymentGroup = shift.defaultPaymentGroup;
              const basePriceCents = paymentGroup.priceCents;
              const priceExcludesVat = paymentGroup.priceExcludesVat;
              const vatMultiplier = priceExcludesVat ? 1.14 : 1;
              const isPerGuest = paymentGroup.paymentType === 'per_guest';

              const baseTotal = isPerGuest
                ? (basePriceCents / 100) * reservationDetails.guests
                : (basePriceCents / 100);

              const vatAmount = priceExcludesVat ? baseTotal * 0.14 : 0;
              const finalTotal = baseTotal * vatMultiplier;

              return (
                <div className="deposit-info mt-2">
                  <p className="deposit-hint">
                    <i className="bi bi-info-circle me-1"></i>
                    A deposit of{' '}
                    <strong>
                      EGP {priceExcludesVat ? finalTotal.toFixed(2) : baseTotal.toFixed(2)}
                    </strong>
                    {' '}is required to confirm your reservation.
                    {isPerGuest && (
                      <span className="deposit-breakdown"> ({reservationDetails.guests} × EGP {(basePriceCents / 100).toFixed(2)})</span>
                    )}
                    {priceExcludesVat && (
                      <span className="deposit-vat"> (incl. {vatAmount.toFixed(2)} VAT)</span>
                    )}
                  </p>
                </div>
              );
            })()}
          </div>
          )}

          {/* Preferred Zone - Only show if zones exist AND for regular reservations AND date is not blocked/sold-out */}
          {(!reservationDetails.date || (!isDateBlocked(reservationDetails.date) && !isDateSoldOut(reservationDetails.date))) && (bookingTypeEvents.length === 0 || bookingTypeChoice === 'regular') && zones.length > 0 && (
            <div className="form-section">
              <label className="form-label">Preferred Zone (Optional)</label>
              <p className="form-hint text-muted">Select your preferred seating area</p>
              <div className="zone-pills-scroll">
                <button className="scroll-btn left" onClick={(e) => {
                  e.currentTarget.nextElementSibling.scrollBy({ left: -200, behavior: 'smooth' });
                }}>
                  <i className="bi bi-chevron-left"></i>
                </button>
                <div className="zone-pills-container">
                  <button
                    type="button"
                    className={`zone-pill ${selectedZoneId === null ? 'active' : ''}`}
                    onClick={() => setSelectedZoneId(null)}
                  >
                    No Preference
                  </button>
                  {zones.map(zone => (
                    <button
                      key={zone.id}
                      type="button"
                      className={`zone-pill ${selectedZoneId === zone.id ? 'active' : ''}`}
                      onClick={() => setSelectedZoneId(zone.id)}
                    >
                      {zone.name}
                    </button>
                  ))}
                </div>
                <button className="scroll-btn right" onClick={(e) => {
                  e.currentTarget.previousElementSibling.scrollBy({ left: 200, behavior: 'smooth' });
                }}>
                  <i className="bi bi-chevron-right"></i>
                </button>
              </div>
            </div>
          )}

          {/* Special Occasion - Only show for regular reservations AND date is not blocked/sold-out */}
          {(!reservationDetails.date || (!isDateBlocked(reservationDetails.date) && !isDateSoldOut(reservationDetails.date))) && (bookingTypeEvents.length === 0 || bookingTypeChoice === 'regular') && (
          <div className="form-section">
            <div className="special-occasion-check">
              <input
                type="checkbox"
                className="custom-checkbox"
                id="hasSpecialOccasion"
                checked={hasSpecialOccasion}
                onChange={(e) => {
                  setHasSpecialOccasion(e.target.checked);
                  if (!e.target.checked) {
                    handleStep1Change('specialOccasion', '');
                  }
                }}
              />
              <label className="checkbox-label" htmlFor="hasSpecialOccasion">
                <i className="bi bi-star-fill me-2"></i>
                Special Occasion
              </label>
            </div>
            {hasSpecialOccasion && (
              <div className="mt-3">
                <input
                  type="text"
                  className="form-control modern-input"
                  id="specialOccasion"
                  value={reservationDetails.specialOccasion}
                  onChange={(e) => handleStep1Change('specialOccasion', e.target.value)}
                  placeholder="e.g., Birthday, Anniversary, Engagement..."
                  maxLength={200}
                />
                <small className="form-text text-muted">
                  Let us know what you're celebrating!
                </small>
              </div>
            )}
          </div>
          )}

          {/* Upcoming Events Section - merged internal + external */}
          {(() => {
            const normalizedInternal = events
              .filter(e => e.isActive !== false)
              .map(e => ({
                ...e,
                title: e.name,
                dateFormatted: new Date(e.eventDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
                time: e.startTime ? `${e.startTime} - ${e.endTime}` : '',
                shortDescription: e.description || '',
                isExternal: false,
                _sourceId: `internal-${e.id}`,
              }));
            // Deduplicate: skip external events that match an internal event by name
            const internalNames = new Set(normalizedInternal.map(e => e.title?.toLowerCase().trim()));
            const normalizedExternal = externalEvents
              .filter(e => e.isActive !== false)
              .filter(e => !internalNames.has(e.title?.toLowerCase().trim()))
              .map(e => ({
                ...e,
                isExternal: true,
                _sourceId: `external-${e.id}`,
              }));
            const allEvents = [...normalizedInternal, ...normalizedExternal];
            if (allEvents.length === 0) return null;
            return (
              <div className="form-section events-section-form">
                <h4 className="events-section-title">
                  <i className="bi bi-calendar-event me-2"></i>
                  Upcoming Events
                </h4>
                <div className="events-grid-form">
                  {allEvents.map((event) => (
                    <EventCard
                      key={event._sourceId || event.id}
                      event={event}
                      compact
                      onClick={() => {
                        if (event.isExternal && event.eventUrl) {
                          window.open(event.eventUrl, '_blank', 'noopener,noreferrer');
                        } else {
                          // Internal event: set the date and trigger ticket selection
                          const eventDate = event.eventDate
                            ? toLocalDateString(new Date(event.eventDate))
                            : '';
                          if (eventDate) {
                            if (isDateSoldOut(eventDate)) {
                              handleStep1Change('date', eventDate);
                            } else {
                              setReservationDetails(prev => ({ ...prev, date: eventDate }));
                              setBookingTypeChoice('tickets');
                              setBookingTypeEvents([event]);
                              setSelectedEvent(event);
                              setSelectedTickets([]);
                              setTicketTotal(0);

                              // Scroll the matching date pill into view
                              setDateViewMode('quick');
                              setTimeout(() => {
                                if (quickDatesContainerRef.current) {
                                  const pills = quickDatesContainerRef.current.querySelectorAll('.quick-date-pill');
                                  for (const pill of pills) {
                                    if (pill.classList.contains('active')) {
                                      pill.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
                                      break;
                                    }
                                  }
                                }
                              }, 150);

                              // Scroll to top of form
                              if (formContainerRef.current) {
                                formContainerRef.current.scrollIntoView({ behavior: 'smooth' });
                              }
                            }
                          }
                        }
                      }}
                    />
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Widget Form Note - Only show in widget mode */}
          {isWidget && restaurant?.widgetFormNote && restaurant.widgetFormNote.replace(/<[^>]*>/g, '').trim() && externalEventsLoaded && bookingTypeEvents.length === 0 && (
            <div className="widget-form-note-container">
              <div
                className="widget-form-note-text"
                dangerouslySetInnerHTML={{ __html: restaurant.widgetFormNote }}
              />
            </div>
          )}

          {/* Event Form House Rules - Only show on event dates */}
          {isWidget && bookingTypeEvents.length > 0 && bookingTypeEvents[0]?.formHouseRules && bookingTypeEvents[0].formHouseRules.replace(/<[^>]*>/g, '').trim() && (
            <div className="widget-form-note-container">
              <div
                className="widget-form-note-text"
                dangerouslySetInnerHTML={{ __html: bookingTypeEvents[0].formHouseRules }}
              />
            </div>
          )}

          {/* Next Button - Only show for regular reservations AND date is not blocked/sold-out */}
          {(!reservationDetails.date || (!isDateBlocked(reservationDetails.date) && !isDateSoldOut(reservationDetails.date))) && (bookingTypeEvents.length === 0 || bookingTypeChoice === 'regular') && (
            <button type="button" className="btn btn-next" onClick={handleNext}>
              Next
            </button>
          )}
        </div>
      )}

      {step === 2 && (
        <form onSubmit={handleSubmit} className="step-content step-2-content">
          <h4 className="step-title">Step 2: Your Information</h4>

          <div className="form-section">
            <div className="row">
              <div className="col-md-6 mb-3">
                <label htmlFor="firstName" className="form-label">First Name</label>
                <input
                  type="text"
                  className="form-control modern-input"
                  id="firstName"
                  value={personalInfo.firstName}
                  onChange={(e) => handleStep2Change('firstName', e.target.value)}
                  placeholder="Your First Name"
                  required
                />
              </div>
              <div className="col-md-6 mb-3">
                <label htmlFor="lastName" className="form-label">Last Name</label>
                <input
                  type="text"
                  className="form-control modern-input"
                  id="lastName"
                  value={personalInfo.lastName}
                  onChange={(e) => handleStep2Change('lastName', e.target.value)}
                  placeholder="Your Last Name"
                  required
                />
              </div>
            </div>

            <div className="mb-3">
              <label htmlFor="email" className="form-label">Email</label>
              <input
                type="email"
                className="form-control modern-input"
                id="email"
                value={personalInfo.email}
                onChange={(e) => handleStep2Change('email', e.target.value)}
                placeholder="Your Email Address"
                required
              />
            </div>

            <div className="mb-3">
              <label htmlFor="phone" className="form-label">Phone Number</label>
              <PhoneInput
                country={'eg'}
                value={personalInfo.phone}
                onChange={(phone) => handleStep2Change('phone', '+' + phone)}
                inputProps={{
                  id: 'phone',
                  required: true,
                }}
                containerClass="phone-input-container"
                inputClass="form-control modern-input"
                buttonClass="phone-input-button"
                enableSearch={true}
                searchPlaceholder="Search country..."
                preferredCountries={['eg', 'sa', 'ae', 'us', 'gb']}
              />
            </div>

            <div className="mb-3">
              <div
                className="collapsible-header"
                onClick={() => setAllergiesExpanded(!allergiesExpanded)}
              >
                <label className="form-label mb-0">
                  <i className={`bi bi-chevron-${allergiesExpanded ? 'down' : 'right'} me-2`}></i>
                  Please let us know if you have any allergies
                  {personalInfo.allergies.length > 0 && (
                    <span className="badge ms-2">
                      {personalInfo.allergies.length} selected
                    </span>
                  )}
                </label>
              </div>
              {allergiesExpanded && (
                <div className="multi-select-group mt-2">
                  {['Peanuts', 'Shellfish', 'Dairy', 'Gluten', 'Eggs', 'Soy', 'Tree Nuts', 'Fish', 'Other'].map((allergy) => (
                    <div key={allergy} className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`allergy-${allergy}`}
                        checked={personalInfo.allergies.includes(allergy)}
                        onChange={(e) => {
                          const newAllergies = e.target.checked
                            ? [...personalInfo.allergies, allergy]
                            : personalInfo.allergies.filter(a => a !== allergy);
                          handleStep2Change('allergies', newAllergies);
                        }}
                      />
                      <label className="form-check-label" htmlFor={`allergy-${allergy}`}>
                        {allergy}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mb-3">
              <div
                className="collapsible-header"
                onClick={() => setDietaryExpanded(!dietaryExpanded)}
              >
                <label className="form-label mb-0">
                  <i className={`bi bi-chevron-${dietaryExpanded ? 'down' : 'right'} me-2`}></i>
                  Any dietary preference
                  {personalInfo.dietaryPreference.length > 0 && (
                    <span className="badge bg-info ms-2">
                      {personalInfo.dietaryPreference.length} selected
                    </span>
                  )}
                </label>
              </div>
              {dietaryExpanded && (
                <div className="multi-select-group mt-2">
                  {['Vegetarian', 'Vegan', 'Halal',  'Pescatarian', 'Gluten-Free', 'Dairy-Free'].map((preference) => (
                    <div key={preference} className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`dietary-${preference}`}
                        checked={personalInfo.dietaryPreference.includes(preference)}
                        onChange={(e) => {
                          const newPreferences = e.target.checked
                            ? [...personalInfo.dietaryPreference, preference]
                            : personalInfo.dietaryPreference.filter(p => p !== preference);
                          handleStep2Change('dietaryPreference', newPreferences);
                        }}
                      />
                      <label className="form-check-label" htmlFor={`dietary-${preference}`}>
                        {preference}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mb-4">
              <label htmlFor="comments" className="form-label">Additional Requests/Comments:</label>
              <textarea
                className="form-control modern-textarea"
                id="comments"
                rows="4"
                value={personalInfo.comments}
                onChange={(e) => handleStep2Change('comments', e.target.value)}
                placeholder="Any special requests or comments..."
              ></textarea>
            </div>
          </div>

          {/* Widget Form Note - Also show in step 2 */}
          {isWidget && restaurant?.widgetFormNote && restaurant.widgetFormNote.replace(/<[^>]*>/g, '').trim() && externalEventsLoaded && bookingTypeEvents.length === 0 && (
            <div className="widget-form-note-container">
              <div
                className="widget-form-note-text"
                dangerouslySetInnerHTML={{ __html: restaurant.widgetFormNote }}
              />
            </div>
          )}

          {/* Event Form House Rules - Also show in step 2 */}
          {isWidget && bookingTypeEvents.length > 0 && bookingTypeEvents[0]?.formHouseRules && bookingTypeEvents[0].formHouseRules.replace(/<[^>]*>/g, '').trim() && (
            <div className="widget-form-note-container">
              <div
                className="widget-form-note-text"
                dangerouslySetInnerHTML={{ __html: bookingTypeEvents[0].formHouseRules }}
              />
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-back"
              onClick={() => {
                setStep(1);
                scrollToTop();
              }}
            >
              Back
            </button>
            {!isLoggedIn && !isWidget && (
              <button
                type="button"
                className="btn btn-submit"
                onClick={handleLoginRedirect}
              >
                <i className="bi bi-box-arrow-in-right me-2"></i>
                Login
              </button>
            )}
            <button
              type="submit"
              className="btn btn-submit btn-primary-submit"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Processing...
                </>
              ) : (
                (isWidget || isLoggedIn) ? 'Complete Reservation' : 'Continue as a guest'
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default ReservationForm;
